import type { ReactElement } from "react";
import { Link, useSearchParams } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { queryValue } from "../../app/navigation.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { ApiState, titleFor } from "../../components/api-state.js";
import { useRowLink } from "../../components/row-link.js";
import { adminCenterUrls } from "../admin-center/urls.js";

import { invoiceApi, invoiceEntryOf, invoiceUrls, type InvoiceEntry } from "./urls.js";

/** 請求書 1 行（<c>GET /api/tenants/{tenantId}/invoices</c>。サーバの ApiInvoiceRow と同じ欄）。 */
export interface InvoiceRow {
    readonly tenantId: number;
    /** 請求月（yyyyMM）。1 通の画面の URL に載せる。 */
    readonly id: string;
    readonly monthLabel: string;
    readonly invoiceNumber: string;
    readonly paymentMethodLabel: string;
    readonly totalLabel: string;
}

export interface InvoiceList {
    readonly tenantId: number;
    readonly tenantTitle: string;
    readonly invoices: readonly InvoiceRow[];
}

/** 入り方ごとの「〜へ戻る」（docs/screens/31）。 */
function backOf(entry: InvoiceEntry, tenantId: number): { readonly to: string; readonly label: string } {
    if (entry === "center") {
        return { to: adminCenterUrls.tenant(tenantId), label: "管理センターの契約へ戻る" };
    }
    return entry === "menu"
        ? { to: invoiceUrls.menu, label: "請求書のメニューへ戻る" }
        : { to: invoiceUrls.tenants, label: "契約一覧へ戻る" };
}

/** 1 通を開く URL。入り方の印を引き継ぐ（1 通の「〜へ戻る」が同じ入り方の一覧・画面を向く）。 */
function showOf(entry: InvoiceEntry, tenantId: number, id: string): string {
    if (entry === "center") {
        return invoiceUrls.showFromCenter(tenantId, id);
    }
    return entry === "menu" ? invoiceUrls.showFromMenu(tenantId, id) : invoiceUrls.show(tenantId, id);
}

/**
 * 請求書の一覧。
 * <b>誰が開けるかは API が決める</b>（開けなければ 404 →「見つからない」）。
 */
export function InvoiceListPage(): ReactElement | null {
    const [search] = useSearchParams();
    const data = useApiData<InvoiceList>(invoiceApi.list(queryValue(search, "tenantId") ?? ""));
    usePageMeta(titleFor(data, "請求書"));
    const rowLink = useRowLink();

    if (data.state !== "ready") {
        return <ApiState data={data} what="請求書" />;
    }
    const list = data.value;
    // 管理センターの契約の画面・請求書のメニューから開いたときは、そこへ戻す。1 通を開くときも印を引き継ぐ
    const entry = invoiceEntryOf(queryValue(search, "from"));
    const back = backOf(entry, list.tenantId);

    return (
        <div className="page max-w-6xl">
            <p className="mb-4"><Link to={back.to} className="btn-secondary btn-sm">{back.label}</Link></p>
            <div className="page-header">
                <div>
                    <h1 className="page-title">請求書</h1>
                    <p className="page-subtitle">{list.tenantTitle}</p>
                </div>
            </div>

            {list.invoices.length === 0
                ? (
                    <div className="empty-state">
                        <p className="text-slate-500">発行済みの請求書はありません。</p>
                    </div>
                )
                : (
                    <div className="table-card">
                        <div className="table-scroll">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th scope="col">請求月</th>
                                        <th scope="col">請求書番号</th>
                                        <th scope="col">お支払方法</th>
                                        <th scope="col" className="cell-number">合計（税込）</th>
                                        <th scope="col"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {list.invoices.map((invoice) => {
                                        const href = showOf(entry, invoice.tenantId, invoice.id);
                                        return (
                                            // 行ごと押せる。**「表示」は残す**——キーボードと読み上げはそちらを辿る
                                            <tr key={invoice.id} data-row-href={href} onClick={rowLink(href)}>
                                                <td className="font-medium text-ink-800">{invoice.monthLabel}</td>
                                                <td className="text-slate-500">{invoice.invoiceNumber}</td>
                                                <td>{invoice.paymentMethodLabel}</td>
                                                <td className="cell-number font-semibold">{invoice.totalLabel}</td>
                                                <td className="cell-actions">
                                                    <Link className="btn-secondary btn-sm" to={href}>表示</Link>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
        </div>
    );
}
